// =====================================================================
// RH — Dimensionamento de Escala: A PALETA DA GRADE (arquivo compartilhado)
// =====================================================================
// ⚠️⚠️ ESTE ARQUIVO É LIDO PELOS DOIS LADOS, e é o ÚNICO do módulo que faz isso:
//    a tela o importa (`escala-grade-utils.js`) e o BACKEND o exige
//    (`server/rh/dimensionamento-escala/pdf-escala.js`, via `require('../../../src/components/rh/dimensionamento-escala/escala-paleta.js')`).
//
//    Por que ele atravessa a fronteira, quando o resto do módulo é ESPELHO
//    (duas cópias com teste travando as duas): porque aqui a divergência não
//    seria um número errado — seria o PAPEL SAIR COM OUTRAS CORES QUE A TELA.
//    Rodrigo, 15/09: "eu quero ela no padrão gráfico que nós fizemos no site,
//    mesma padronização, não precisa inventar a roda." Duas cópias de uma
//    tabela de cores divergem no primeiro ajuste de tom que alguém fizer na
//    tela sem lembrar do PDF, e ninguém percebe até imprimir.
//
// ⚠️ Por isso ele é **CommonJS** (`module.exports`) e não ESM como o resto do
//    `src/`: o Node do backend precisa conseguir `require` nele. O webpack lê
//    CommonJS sem reclamar, então a tela não perde nada.
//
// ⚠️ MOVER OU RENOMEAR ESTE ARQUIVO QUEBRA O PDF EM PRODUÇÃO. Há teste no
//    backend (`server/tests/rh/escala-paleta-compartilhada.test.js`) que o
//    carrega pelo mesmo caminho relativo — se o arquivo andar, o teste cai
//    antes do deploy, em vez de o PDF sair cinza sem ninguém notar.
//
// A REGRA DA COR (Rodrigo, 14/09):
//   · o MATIZ (hue) é do CARGO — bate o olho e sabe de quem se trata;
//   · a LUMINOSIDADE separa a escala: 12x36 mais escuro, 5x2 no mesmo matiz,
//     mais claro. São parentes visíveis, não cores alheias;
//   · não há gradiente por linha — duas linhas de 12x36 do mesmo cargo ficam
//     IGUAIS, e quem as separa é o rótulo.

// ⚠️⚠️ MEXER NA TABELA TEM PISO: a fonte do número é uma só e escura
//    (TEXTO_CELULA), então todo tom precisa manter contraste WCAG AA (≥ 4,5:1)
//    com ela. Os valores abaixo são o MAIS ESCURO que cada matiz alcança
//    mantendo contraste ≥ 5,0 — calculado, não escolhido a olho, e há teste
//    medindo cada par.
// ⚠️ O ROXO parece mais claro que os outros e NÃO É DESCUIDO: no MESMO
//    contraste com a fonte escura, o verde cabe em 41% de luminosidade e o roxo
//    precisa de 63%. Escurecê-lo até "parecer" com o verde derruba o contraste
//    abaixo do legível (roxo a 60% já cai para 4,4).
const PALETA_CARGO = {
    //                        matiz    sat    12x36     5x2      contraste 12x36 / 5x2
    'ATENDENTE LANCHONETE': { hue: 214, sat: 62, lum12: 57.5, lum5: 69.5 },  // azul    5,00 / 7,46
    'AUX. SERVICOS GERAIS': { hue: 150, sat: 48, lum12: 41,   lum5: 53 },    // verde   5,10 / 7,83
    'ESTOQUISTA':           { hue: 272, sat: 48, lum12: 63,   lum5: 75 },    // roxo    5,01 / 7,94
    'ANFITRIAO':            { hue: 28,  sat: 64, lum12: 47.5, lum5: 59.5 },  // laranja 5,03 / 7,01
    'MENOR APRENDIZ':       { hue: 330, sat: 50, lum12: 60,   lum5: 72 },    // rosa    5,01 / 7,55  (fase 2)
};

// Cargo fora da tabela ainda ganha cor — determinística, para não nascer sem.
const PADRAO_CARGO = { sat: 40, lum12: 66, lum5: 78 };   // conservador: claro para qualquer matiz

function hueDoCargo(cargo) {
    if (PALETA_CARGO[cargo]) return PALETA_CARGO[cargo].hue;
    let h = 0;
    for (let i = 0; i < String(cargo).length; i++) h = (h * 31 + String(cargo).charCodeAt(i)) % 360;
    return h;
}

// O tom de um cargo numa escala. É a fonte única da cor da grade — na tela e no papel.
function tomDoCargo(cargo, escala) {
    const base = PALETA_CARGO[cargo] || Object.assign({}, PADRAO_CARGO, { hue: hueDoCargo(cargo) });
    const lum = escala === '5X2' ? base.lum5 : base.lum12;
    return { hue: base.hue, sat: base.sat, lum, base: `hsl(${base.hue} ${base.sat}% ${lum}%)` };
}

// ⚠️ A COR DO BREAK É UMA SÓ POR CARGO — decisão do Rodrigo (14/09): "a parte do
//    break precisa ser 1 cor só por cargo, ignorar a diferença entre 5x2 e
//    12x36". Usa o MATIZ do cargo com saturação e luminosidade FIXAS: o olho lê
//    "aqui tem gente almoçando" sem se perguntar de que escala é.
const BREAK_SAT = 34;
const BREAK_LUM = 90;
const corDoBreak = (hue) => `hsl(${hue} ${BREAK_SAT}% ${BREAK_LUM}%)`;

// A célula de um grupo. Duas cores possíveis, e só duas:
//   · todo mundo no chão  → a cor da LINHA (varia por escala)
//   · alguém em break     → a cor do BREAK daquele CARGO (uma só)
function corDaCelula(cor, valor, qtd) {
    if (!cor || valor === null || valor === undefined) return 'transparent';
    if (!qtd) return '#f1f5f9';
    return valor < qtd ? corDoBreak(cor.hue) : cor.base;
}

// ⚠️ UMA COR SÓ para o número, sempre (pedido do Rodrigo, 14/09). O que garante
//    a leitura é a paleta ser clara, não o texto se adaptar.
const TEXTO_CELULA = '#111827';
const textoDaCelula = () => TEXTO_CELULA;

// ⚠️ A linha TOTAL usa OUTRA escala de cor, de propósito: ela não representa um
// cargo, representa MOVIMENTO. Verde = hora fraca, amarelo = média, vermelho =
// pico. Misturá-la na paleta dos cargos faria o olho ler a soma como se fosse
// mais um grupo.
function corDoTotal(valor, max) {
    if (!max || valor === 0) return '#f1f5f9';
    const t = Math.min(1, valor / max);
    const hue = 120 - t * 120;          // 120 verde → 60 amarelo → 0 vermelho
    return `hsl(${hue} 78% ${86 - t * 22}%)`;
}

const textoDoTotal = (valor, max) => (!max || valor === 0 ? '#94a3b8' : TEXTO_CELULA);

// ---------------------------------------------------------------------
// ⚠️ O SEMÁFORO DA PROPORÇÃO 12x36 × 5x2  (DEM-00024)
// ---------------------------------------------------------------------
// Mora AQUI, na paleta compartilhada, pelo mesmo motivo das cores da grade: o
// chip da TELA e o bloco do PDF têm de sair com a MESMA cor. Duas cópias
// divergiriam no primeiro ajuste de tom e ninguém veria até imprimir.
//
// ⚠️ A régua (que % é verde/amarelo/vermelho) NÃO está aqui — ela é conta, e
//    conta é espelho testado (escala-motor.js + escala-grade-utils.js). Aqui
//    mora só a COR de cada faixa. A faixa chega pronta ('verde'|'amarelo'|
//    'vermelho') de `classificarProporcao`.
//
// Cada faixa é um TRIO fundo/texto/ponto: fundo claro com texto escuro (o mesmo
// padrão dos avisos da tela) e um ponto saturado que lê como luz de semáforo.
// O par texto↔fundo mantém contraste WCAG AA (≥4,5:1) — há teste medindo.
const CORES_SEMAFORO = {
    verde:    { fundo: '#dcfce7', texto: '#166534', ponto: '#16a34a', rotulo: 'equilibrada' },
    amarelo:  { fundo: '#fef9c3', texto: '#854d0e', ponto: '#eab308', rotulo: 'atenção' },
    vermelho: { fundo: '#fee2e2', texto: '#991b1b', ponto: '#dc2626', rotulo: 'fora da meta' },
};

// Faixa → trio de cores. Faixa nula/desconhecida (sem base para calcular) cai
// num cinza neutro, para a tela mostrar "—" sem pintar um semáforo sobre o nada.
// ⚠️ texto slate-600 (não slate-500): sobre o fundo slate-100 o 500 dá 4,34 e
//    não passa AA. Medido no teste da paleta compartilhada.
const SEMAFORO_NEUTRO = { fundo: '#f1f5f9', texto: '#475569', ponto: '#cbd5e1', rotulo: 'sem base' };
function corSemaforo(faixa) {
    return CORES_SEMAFORO[faixa] || SEMAFORO_NEUTRO;
}

module.exports = {
    PALETA_CARGO,
    PADRAO_CARGO,
    hueDoCargo,
    tomDoCargo,
    corDoBreak,
    corDaCelula,
    TEXTO_CELULA,
    textoDaCelula,
    corDoTotal,
    textoDoTotal,
    CORES_SEMAFORO,
    SEMAFORO_NEUTRO,
    corSemaforo,
};
